import type { FleetRunner } from './claude-fleet.types'
import { isRunnerOwner, runnerOwnerLabel } from './claude-fleet.runners'

/** A laptop runner a run can be sent to, by its owner. */
export interface FleetRunTarget {
  email: string
  name: string
  /** The activities this runner takes; absent from a fleet reply older than the field. */
  kinds?: string[]
}

/**
 * GET /fleet/runners/targets: the runners the viewer may send a run to. Only engineering leadership and
 * fleet operators may send one to someone else's runner, and the fleet refuses anyone else who tries.
 */
export interface FleetRunTargets {
  mayTargetOthers: boolean
  /** The viewer's own runner, while it is live. */
  self?: FleetRunTarget & { live: true }
  /** Other people's live runners; empty unless the viewer may target them. */
  others: FleetRunTarget[]
}

/** A Run on choice: what the dropdown shows and the `runOn` value the fleet is sent. */
export interface FleetRunOnOption {
  title: string
  value: string
}

/** Any live runner may take it. */
export const RUN_ON_ANY = 'any'
/** The fleet's own settings decide where it runs; sent as no `runOn` at all. */
export const RUN_ON_FLEET = 'fleet'
export const RUN_ON_AGENTCORE = 'agentcore'

export const RUN_ON_WAIT_NOTE = 'A run for one runner waits 15 minutes for it, then any runner may take it.'

/** Only a run sent to one runner, by its owner's email, waits for that runner. */
export function isOneRunnerRunOn(runOn: string): boolean {
  return runOn !== RUN_ON_ANY && runOn !== RUN_ON_FLEET && runOn !== RUN_ON_AGENTCORE
}

export const NO_RUN_TARGETS: FleetRunTargets = { mayTargetOthers: false, others: [] }

/** The fleet's reply, with anything missing read as "no runners to choose". */
export function normalizeRunTargets(reply: Partial<FleetRunTargets> | null | undefined): FleetRunTargets {
  const self = reply?.self?.email ? { ...reply.self, live: true as const } : undefined
  return {
    mayTargetOthers: reply?.mayTargetOthers === true,
    ...(self ? { self } : {}),
    others: (reply?.others ?? []).filter(target => target.email && !isRunnerOwner(target.email, self?.email))
  }
}

/**
 * The targets a fleet service without GET /runners/targets allows: the viewer's own live runner, from the
 * Runners tab's list, and nobody else's.
 */
export function runTargetsFromRunners(runners: FleetRunner[], viewerEmail: string | null | undefined): FleetRunTargets {
  const own = runners.find(runner => runner.live && isRunnerOwner(runner.owner, viewerEmail))
  if (!own) return NO_RUN_TARGETS
  const self = { email: own.owner, name: runnerOwnerLabel(own), live: true as const, kinds: [...own.kinds] }
  return { mayTargetOthers: false, self, others: [] }
}

const runnerOption = (target: FleetRunTarget, title: string): FleetRunOnOption => ({ title, value: target.email })

const otherTargets = (targets: FleetRunTargets): FleetRunTarget[] => (targets.mayTargetOthers ? targets.others : [])

/** A runner takes an activity it lists; one that lists none is left to the fleet to refuse. */
const takes = (target: FleetRunTarget, activity: string | null): boolean =>
  !target.kinds || !activity || target.kinds.includes(activity)

/**
 * Where a Deliver can run: the viewer's runner while it is live (the fleet refuses an email with no live
 * runner), any runner, then other people's runners for those allowed to pick them.
 */
export function deliverRunOnOptions(targets: FleetRunTargets): FleetRunOnOption[] {
  return [
    ...(targets.self ? [runnerOption(targets.self, 'My runner')] : []),
    { title: 'Any runner', value: RUN_ON_ANY },
    ...otherTargets(targets).map(target => runnerOption(target, `${target.name}'s runner`))
  ]
}

/** A Deliver goes to the viewer's runner while it is live, else to any runner. */
export function deliverRunOnDefault(targets: FleetRunTargets): string {
  return targets.self?.email ?? RUN_ON_ANY
}

/**
 * Where any other run can go: wherever the fleet settings say, AgentCore, or one live runner that takes the
 * activity. A Deliver goes through the ticket-action inbox, which every runner reads, so it is not filtered.
 */
export function fleetRunOnOptions(targets: FleetRunTargets, activity: string | null): FleetRunOnOption[] {
  const own = targets.self && takes(targets.self, activity) ? [runnerOption(targets.self, 'My runner')] : []
  const others = otherTargets(targets)
    .filter(target => takes(target, activity))
    .map(target => runnerOption(target, `${target.name}'s runner`))
  return [{ title: 'Fleet decides', value: RUN_ON_FLEET }, { title: 'AgentCore', value: RUN_ON_AGENTCORE }, ...own, ...others]
}
